import { z } from 'zod';
import { AlertEventStatus, AlertSeverity, AlertType, NotificationChannel } from '../enums.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/** Optional active window for a rule (tenant timezone). `from > to` means an overnight window. */
export const AlertScheduleSchema = z.object({
  days: z.array(z.number().int().min(0).max(6)).min(1).max(7),
  from: hhmm,
  to: hhmm,
});
export type AlertSchedule = z.infer<typeof AlertScheduleSchema>;

/** P1 rule types the engine implements (ROUTE_DEVIATION needs planned routes → P2). */
export const ALERT_RULE_TYPES = [
  'SPEEDING',
  'GEOFENCE_ENTER',
  'GEOFENCE_EXIT',
  'STOP_TOO_LONG',
  'UNAUTHORIZED_STOP',
  'FIRST_START_OF_DAY',
  'AFTER_HOURS_DRIVING',
  'DEVICE_OFFLINE',
  'PHONE_NOT_REPORTING',
  'IGNITION_ON',
  'IGNITION_OFF',
  'MAINTENANCE_DUE',
  'DOCUMENT_EXPIRING',
  'DRIVER_LICENSE_EXPIRING',
  'EXPENSE_PENDING',
  'TRIP_LATE',
  'TRIP_NOT_STARTED',
] as const satisfies readonly AlertType[];
export type AlertRuleType = (typeof ALERT_RULE_TYPES)[number];

export interface AlertConfigField {
  key: string;
  type: 'number' | 'boolean';
  min?: number;
  max?: number;
  default: number | boolean;
  unit?: 'kph' | 'min' | 'hours' | 'days' | 'km';
}

export interface AlertTypeMeta {
  group: 'movement' | 'geofence' | 'connectivity' | 'fleet' | 'trips' | 'finance';
  defaultSeverity: AlertSeverity;
  /** evaluated by a cron (true) or by ingest events (false) */
  scheduled: boolean;
  /** rule.placeIds narrows the places considered */
  usesPlaces: boolean;
  /** vehicle scope (allVehicles / vehicleIds / groupIds) is meaningful */
  usesVehicles: boolean;
  config: AlertConfigField[];
}

/** Catalog driving both the engine and the rule editor. Keys in `config` are the only ones the engine reads. */
export const ALERT_TYPE_CATALOG: Record<AlertRuleType, AlertTypeMeta> = {
  SPEEDING: { group: 'movement', defaultSeverity: 'WARNING', scheduled: false, usesPlaces: false, usesVehicles: true, config: [{ key: 'speedKph', type: 'number', min: 20, max: 250, default: 100, unit: 'kph' }] },
  GEOFENCE_ENTER: { group: 'geofence', defaultSeverity: 'INFO', scheduled: false, usesPlaces: true, usesVehicles: true, config: [] },
  GEOFENCE_EXIT: { group: 'geofence', defaultSeverity: 'INFO', scheduled: false, usesPlaces: true, usesVehicles: true, config: [] },
  STOP_TOO_LONG: { group: 'movement', defaultSeverity: 'INFO', scheduled: true, usesPlaces: false, usesVehicles: true, config: [{ key: 'durationMin', type: 'number', min: 5, max: 1440, default: 30, unit: 'min' }] },
  UNAUTHORIZED_STOP: { group: 'geofence', defaultSeverity: 'WARNING', scheduled: true, usesPlaces: true, usesVehicles: true, config: [{ key: 'durationMin', type: 'number', min: 1, max: 1440, default: 10, unit: 'min' }] },
  FIRST_START_OF_DAY: { group: 'movement', defaultSeverity: 'INFO', scheduled: false, usesPlaces: false, usesVehicles: true, config: [] },
  AFTER_HOURS_DRIVING: { group: 'movement', defaultSeverity: 'WARNING', scheduled: false, usesPlaces: false, usesVehicles: true, config: [] },
  DEVICE_OFFLINE: { group: 'connectivity', defaultSeverity: 'CRITICAL', scheduled: false, usesPlaces: false, usesVehicles: true, config: [] },
  PHONE_NOT_REPORTING: { group: 'connectivity', defaultSeverity: 'WARNING', scheduled: false, usesPlaces: false, usesVehicles: true, config: [] },
  IGNITION_ON: { group: 'movement', defaultSeverity: 'INFO', scheduled: false, usesPlaces: false, usesVehicles: true, config: [] },
  IGNITION_OFF: { group: 'movement', defaultSeverity: 'INFO', scheduled: false, usesPlaces: false, usesVehicles: true, config: [] },
  MAINTENANCE_DUE: { group: 'fleet', defaultSeverity: 'WARNING', scheduled: true, usesPlaces: false, usesVehicles: true, config: [] },
  DOCUMENT_EXPIRING: { group: 'fleet', defaultSeverity: 'WARNING', scheduled: true, usesPlaces: false, usesVehicles: true, config: [] },
  DRIVER_LICENSE_EXPIRING: { group: 'fleet', defaultSeverity: 'WARNING', scheduled: true, usesPlaces: false, usesVehicles: false, config: [{ key: 'daysBefore', type: 'number', min: 1, max: 180, default: 30, unit: 'days' }] },
  EXPENSE_PENDING: { group: 'finance', defaultSeverity: 'INFO', scheduled: true, usesPlaces: false, usesVehicles: false, config: [{ key: 'olderThanHours', type: 'number', min: 1, max: 720, default: 24, unit: 'hours' }] },
  TRIP_LATE: { group: 'trips', defaultSeverity: 'WARNING', scheduled: true, usesPlaces: false, usesVehicles: true, config: [{ key: 'graceMin', type: 'number', min: 0, max: 1440, default: 0, unit: 'min' }] },
  TRIP_NOT_STARTED: { group: 'trips', defaultSeverity: 'WARNING', scheduled: true, usesPlaces: false, usesVehicles: true, config: [{ key: 'graceMin', type: 'number', min: 0, max: 1440, default: 30, unit: 'min' }] },
};

/** Reads a numeric config value with the catalog default as fallback. */
export function ruleConfigNumber(type: AlertRuleType, config: Record<string, unknown> | null | undefined, key: string): number {
  const v = config?.[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const f = ALERT_TYPE_CATALOG[type].config.find((c) => c.key === key);
  return typeof f?.default === 'number' ? f.default : 0;
}

export const AlertRuleInputSchema = z.object({
  name: z.string().min(2).max(120),
  type: z.enum(ALERT_RULE_TYPES),
  severity: z.enum(AlertSeverity).optional(),
  isEnabled: z.boolean().default(true),
  allVehicles: z.boolean().default(true),
  vehicleIds: z.array(z.string().uuid()).max(500).default([]),
  groupIds: z.array(z.string().uuid()).max(100).default([]),
  placeIds: z.array(z.string().uuid()).max(500).default([]),
  config: z.record(z.union([z.number(), z.boolean()])).default({}),
  schedule: AlertScheduleSchema.nullable().optional(),
  cooldownSec: z.number().int().min(0).max(86_400).default(300),
  recipientUserIds: z.array(z.string().uuid()).max(200).default([]),
  recipientRoleCodes: z.array(z.string().min(1).max(40)).max(20).default([]),
  channels: z.array(z.enum(NotificationChannel)).min(1).default(['IN_APP', 'PUSH']),
  notifyDriver: z.boolean().default(false),
});
export type AlertRuleInput = z.infer<typeof AlertRuleInputSchema>;

export const UpdateAlertRuleSchema = AlertRuleInputSchema.partial();
export type UpdateAlertRule = z.infer<typeof UpdateAlertRuleSchema>;

export const AlertRulesQuerySchema = z.object({
  type: z.enum(ALERT_RULE_TYPES).optional(),
  enabled: z.coerce.boolean().optional(),
});
export type AlertRulesQuery = z.infer<typeof AlertRulesQuerySchema>;

export const AlertsQuerySchema = z.object({
  status: z.enum(AlertEventStatus).optional(),
  type: z.enum(AlertType).optional(),
  severity: z.enum(AlertSeverity).optional(),
  vehicleId: z.string().uuid().optional(),
  driverId: z.string().uuid().optional(),
  tripId: z.string().uuid().optional(),
  assignedToId: z.string().uuid().optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type AlertsQuery = z.infer<typeof AlertsQuerySchema>;

export const CloseAlertSchema = z.object({ note: z.string().max(500).nullable().optional() });
export type CloseAlert = z.infer<typeof CloseAlertSchema>;

export const AssignAlertSchema = z.object({ userId: z.string().uuid().nullable() });
export type AssignAlert = z.infer<typeof AssignAlertSchema>;

export const BulkAlertActionSchema = z.object({ ids: z.array(z.string().uuid()).min(1).max(200), note: z.string().max(500).nullable().optional() });
export type BulkAlertAction = z.infer<typeof BulkAlertActionSchema>;

export const ALERT_EVENT_TRANSITIONS: Record<(typeof AlertEventStatus)[number], readonly string[]> = {
  OPEN: ['acknowledge', 'close', 'assign'],
  ACKNOWLEDGED: ['close', 'assign'],
  CLOSED: [],
};

// ───────────────────────────── pure helpers ─────────────────────────────

/** Day-of-week (0 = Sunday) and minutes-since-midnight of an instant in a timezone. */
export function localClock(at: Date, timezone: string): { day: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
  return { day, minutes: Number(get('hour')) * 60 + Number(get('minute')) };
}

const toMin = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));

/**
 * True when the instant falls inside the rule schedule (no schedule → always). Overnight windows
 * (e.g. 22:00→06:00) are attributed to the day they start on.
 */
export function matchesSchedule(schedule: AlertSchedule | null | undefined, at: Date, timezone: string): boolean {
  if (!schedule) return true;
  const { day, minutes } = localClock(at, timezone);
  const from = toMin(schedule.from);
  const to = toMin(schedule.to);
  if (from <= to) return schedule.days.includes(day) && minutes >= from && minutes < to;
  // overnight: [from, 24h) of `day` or [0, to) of the following day
  if (minutes >= from) return schedule.days.includes(day);
  return minutes < to && schedule.days.includes((day + 6) % 7);
}

export interface RuleScope {
  allVehicles: boolean;
  vehicleIds: string[];
  groupIds: string[];
}

/** Whether a rule targets a vehicle (explicit list or its group; allVehicles wins). */
export function ruleCoversVehicle(rule: RuleScope, vehicle: { id: string; groupId: string | null } | null): boolean {
  if (rule.allVehicles) return true;
  if (!vehicle) return false;
  if (rule.vehicleIds.includes(vehicle.id)) return true;
  return vehicle.groupId != null && rule.groupIds.includes(vehicle.groupId);
}

/** Bucketed dedupe key so two triggers inside one cooldown window collapse into a single event. */
export function cooldownKey(ruleId: string, subject: string, at: Date, cooldownSec: number): string {
  const bucket = cooldownSec > 0 ? Math.floor(at.getTime() / (cooldownSec * 1000)) : at.getTime();
  return `${ruleId}:${subject}:${bucket}`;
}

/** Rules every tenant starts with (also used by the DB seed). */
export const DEFAULT_ALERT_RULES: { name: string; type: AlertRuleType; severity: AlertSeverity; config: Record<string, number | boolean>; isEnabled?: boolean }[] = [
  { name: 'تجاوز السرعة 100 كم/س', type: 'SPEEDING', severity: 'WARNING', config: { speedKph: 100 } },
  { name: 'توقف أطول من 30 دقيقة', type: 'STOP_TOO_LONG', severity: 'INFO', config: { durationMin: 30 } },
  { name: 'قيادة خارج ساعات العمل', type: 'AFTER_HOURS_DRIVING', severity: 'WARNING', config: {} },
  { name: 'انقطاع الجهاز', type: 'DEVICE_OFFLINE', severity: 'CRITICAL', config: {} },
  { name: 'الهاتف لا يرسل الموقع', type: 'PHONE_NOT_REPORTING', severity: 'WARNING', config: {} },
  { name: 'صيانة مستحقة', type: 'MAINTENANCE_DUE', severity: 'WARNING', config: {} },
  { name: 'مستند على وشك الانتهاء', type: 'DOCUMENT_EXPIRING', severity: 'WARNING', config: {} },
  { name: 'رخصة سائق على وشك الانتهاء', type: 'DRIVER_LICENSE_EXPIRING', severity: 'WARNING', config: { daysBefore: 30 } },
  { name: 'مصروف بانتظار المراجعة', type: 'EXPENSE_PENDING', severity: 'INFO', config: { olderThanHours: 24 } },
  { name: 'رحلة متأخرة عن موعد التسليم', type: 'TRIP_LATE', severity: 'WARNING', config: { graceMin: 0 } },
  { name: 'رحلة لم تبدأ في موعدها', type: 'TRIP_NOT_STARTED', severity: 'WARNING', config: { graceMin: 30 } },
];
